import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'
import { Input, Modal, Spin, Typography, message } from 'antd'
import dayjs from 'dayjs'

import { showActionNotification } from '../../../components/notification'
import DeletePopUp from '../../../components/deletePopUp/deletePopUp'
import {
    createPrototype,
    deletePrototype,
    listPrototypes,
    updatePrototype,
    uploadPrototypeHtmlFile,
} from '../prototype.service'
import {
    PROTOTYPE_TAB_BAR_HOST_ATTR,
    PROTOTYPE_TAB_BAR_SLOT_ATTR,
    attachTabBarSlot,
    removeTabBarHost,
} from './projectTabBarPortal'
import styles from './ProjectPrototype.module.css'

const { Text, Title } = Typography
const { TextArea } = Input

const PAGE_SIZE = 25

const formatDate = (value) => {
    if (!value) return ''
    const parsed = dayjs(value)
    return parsed.isValid() ? parsed.format('D MMM YYYY') : ''
}

const isHtmlFile = (file) => {
    if (!file?.name) return false
    const lower = file.name.toLowerCase()
    return lower.endsWith('.html') || lower.endsWith('.htm')
}

const CreatePrototypeModal = ({ open, confirming, onCancel, onSubmit }) => {
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [file, setFile] = useState(null)
    const inputRef = useRef(null)

    useEffect(() => {
        if (!open) {
            setName('')
            setDescription('')
            setFile(null)
        }
    }, [open])

    const handleCancel = () => {
        if (confirming) return
        onCancel?.()
    }

    const handleOk = () => {
        if (confirming) return
        const trimmed = name.trim()
        if (!trimmed) {
            message.warning('Name is required')
            return
        }
        if (!file || !isHtmlFile(file)) {
            message.warning('Please choose an .html file')
            return
        }
        onSubmit({ name: trimmed, description: description.trim(), file })
    }

    return (
        <Modal
            title={
                <div className={styles.createModalHeader}>
                    <div className={styles.createModalHeaderLeft}>
                        <div className={styles.createModalIcon} aria-hidden>
                            <Icon icon="mdi:vector-polyline" width={20} />
                        </div>
                        <div>
                            <div className={styles.createModalTitle}>
                                Create Prototype
                            </div>
                            <div className={styles.createModalSubtitle}>
                                Upload an HTML file to start annotating
                            </div>
                        </div>
                    </div>
                </div>
            }
            open={open}
            onCancel={handleCancel}
            footer={
                <div className={styles.createModalFooter}>
                    <div className={styles.createModalActions}>
                        <button
                            type="button"
                            onClick={handleCancel}
                            className={styles.createModalCancel}
                            disabled={confirming}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleOk}
                            disabled={confirming}
                            className={`${styles.createModalSubmit} ${
                                confirming
                                    ? styles.createModalSubmitDisabled
                                    : ''
                            }`}
                        >
                            {confirming ? 'Uploading…' : 'Upload & create'}
                        </button>
                    </div>
                </div>
            }
            width={520}
            className="create-prototype-modal"
            destroyOnClose
            centered
            closeIcon={null}
            maskClosable={!confirming}
        >
            <div className={styles.createModalForm}>
                <label className={styles.createModalField}>
                    <span className={styles.createModalLabel}>
                        Name <span className={styles.createModalRequired}>*</span>
                    </span>
                    <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        maxLength={200}
                        placeholder="e.g. Checkout redesign"
                        size="large"
                        disabled={confirming}
                    />
                </label>

                <label className={styles.createModalField}>
                    <span className={styles.createModalLabel}>Description</span>
                    <TextArea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        maxLength={2000}
                        rows={3}
                        placeholder="Add a short description (optional)"
                        disabled={confirming}
                    />
                </label>

                <div className={styles.createModalField}>
                    <span className={styles.createModalLabel}>
                        HTML file{' '}
                        <span className={styles.createModalRequired}>*</span>
                    </span>
                    <div className={styles.fileDrop}>
                        <input
                            ref={inputRef}
                            type="file"
                            accept=".html,.htm,text/html"
                            style={{ display: 'none' }}
                            disabled={confirming}
                            onChange={(e) => {
                                setFile(e.target.files?.[0] || null)
                            }}
                        />
                        <button
                            type="button"
                            className={styles.filePickButton}
                            onClick={() => inputRef.current?.click()}
                            disabled={confirming}
                        >
                            <Icon icon="mdi:file-upload-outline" width={16} />
                            {file ? 'Change HTML file' : 'Choose HTML file'}
                        </button>
                        {file ? (
                            <div className={styles.fileName}>{file.name}</div>
                        ) : (
                            <div className={styles.fileHint}>
                                Only .html / .htm, max 25 MB
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    )
}

const EditPrototypeModal = ({
    open,
    confirming,
    initialName = '',
    initialDescription = '',
    onCancel,
    onSubmit,
}) => {
    const [name, setName] = useState(initialName)
    const [description, setDescription] = useState(initialDescription)

    useEffect(() => {
        if (!open) return
        setName(initialName || '')
        setDescription(initialDescription || '')
    }, [open, initialName, initialDescription])

    const handleCancel = () => {
        if (confirming) return
        onCancel?.()
    }

    const handleOk = () => {
        if (confirming) return
        const trimmed = name.trim()
        if (!trimmed) {
            message.warning('Name is required')
            return
        }
        onSubmit({
            name: trimmed,
            description: description.trim(),
        })
    }

    return (
        <Modal
            title={
                <div className={styles.createModalHeader}>
                    <div className={styles.createModalHeaderLeft}>
                        <div className={styles.createModalIcon} aria-hidden>
                            <Icon icon="mdi:pencil-outline" width={20} />
                        </div>
                        <div>
                            <div className={styles.createModalTitle}>
                                Edit Prototype
                            </div>
                            <div className={styles.createModalSubtitle}>
                                Update name and description
                            </div>
                        </div>
                    </div>
                </div>
            }
            open={open}
            onCancel={handleCancel}
            footer={
                <div className={styles.createModalFooter}>
                    <div className={styles.createModalActions}>
                        <button
                            type="button"
                            onClick={handleCancel}
                            className={styles.createModalCancel}
                            disabled={confirming}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleOk}
                            disabled={confirming}
                            className={`${styles.createModalSubmit} ${
                                confirming
                                    ? styles.createModalSubmitDisabled
                                    : ''
                            }`}
                        >
                            {confirming ? 'Saving…' : 'Save changes'}
                        </button>
                    </div>
                </div>
            }
            width={520}
            className="create-prototype-modal"
            destroyOnClose
            centered
            closeIcon={null}
            maskClosable={!confirming}
        >
            <div className={styles.createModalForm}>
                <label className={styles.createModalField}>
                    <span className={styles.createModalLabel}>
                        Name{' '}
                        <span className={styles.createModalRequired}>*</span>
                    </span>
                    <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        maxLength={200}
                        placeholder="e.g. Checkout redesign"
                        size="large"
                        disabled={confirming}
                        autoFocus
                        onPressEnter={handleOk}
                    />
                </label>

                <label className={styles.createModalField}>
                    <span className={styles.createModalLabel}>Description</span>
                    <TextArea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        maxLength={2000}
                        rows={3}
                        placeholder="Add a short description (optional)"
                        disabled={confirming}
                    />
                </label>
            </div>
        </Modal>
    )
}

const PrototypeList = ({
    items,
    loading,
    loadError,
    onOpen,
    onEdit,
    onDelete,
    onRetry,
    readOnly = false,
    canDelete = false,
}) => {
    if (loading) {
        return (
            <div className={styles.loadingState}>
                <Spin />
                <span>Loading prototypes…</span>
            </div>
        )
    }

    if (loadError) {
        return (
            <div className={styles.errorState}>
                <Icon icon="mdi:alert-circle-outline" width={28} />
                <div>{loadError}</div>
                <button
                    type="button"
                    className={styles.ghostButton}
                    onClick={onRetry}
                >
                    Retry
                </button>
            </div>
        )
    }

    if (!items.length) {
        return (
            <div className={styles.emptyState}>
                <Icon icon="mdi:vector-polyline" width={32} />
                <Title level={5} style={{ margin: 0 }}>
                    No prototypes yet
                </Title>
                <Text type="secondary">
                    {readOnly
                        ? 'Open a prototype to view annotations and comments.'
                        : 'Upload an HTML prototype, then open it in the drawing editor to annotate and comment.'}
                </Text>
            </div>
        )
    }

    return (
        <div className={styles.grid}>
            {items.map((item) => (
                <button
                    key={item._id}
                    type="button"
                    className={styles.card}
                    onClick={() => onOpen(item._id)}
                >
                    <div className={styles.cardTop}>
                        <div className={styles.cardIcon}>
                            <Icon icon="mdi:vector-polyline" width={20} />
                        </div>
                        <div className={styles.cardBody}>
                            <h3 className={styles.cardTitle}>{item.name}</h3>
                            <div className={styles.cardMeta}>
                                {item.createdByName || 'Unknown'}
                                {item.createdAt
                                    ? ` · ${formatDate(item.createdAt)}`
                                    : ''}
                            </div>
                        </div>
                        <div className={styles.cardActions}>
                            {!readOnly ? (
                                <span
                                    role="button"
                                    tabIndex={0}
                                    title="Edit"
                                    className={styles.cardActionBtn}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        onEdit?.(item)
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.stopPropagation()
                                            onEdit?.(item)
                                        }
                                    }}
                                >
                                    <Icon
                                        icon="mdi:pencil-outline"
                                        width={18}
                                        color="#94a3b8"
                                    />
                                </span>
                            ) : null}
                            {canDelete ? (
                                <span
                                    role="button"
                                    tabIndex={0}
                                    title="Delete"
                                    className={styles.cardActionBtn}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        onDelete(item)
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.stopPropagation()
                                            onDelete(item)
                                        }
                                    }}
                                >
                                    <Icon
                                        icon="mdi:trash-can-outline"
                                        width={18}
                                        color="#94a3b8"
                                    />
                                </span>
                            ) : null}
                        </div>
                    </div>
                    <p className={styles.cardDesc}>
                        {item.description || '\u00A0'}
                    </p>
                    <div className={styles.cardStats}>
                        <span className={styles.stat}>
                            <Icon icon="mdi:comment-outline" width={14} />
                            {item.commentCount ?? 0} comments
                        </span>
                        <span className={styles.stat}>
                            <Icon icon="mdi:comment-alert-outline" width={14} />
                            {item.openCommentCount ?? 0} open
                        </span>
                    </div>
                </button>
            ))}
        </div>
    )
}

const ProjectPrototypeListView = ({
    projectId,
    readOnly = false,
    canDelete = false,
    onOpen,
}) => {
    const [items, setItems] = useState([])
    const [loading, setLoading] = useState(false)
    const [loadError, setLoadError] = useState('')
    const [search, setSearch] = useState('')
    const [isNext, setIsNext] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [createOpen, setCreateOpen] = useState(false)
    const [creating, setCreating] = useState(false)
    const [editItem, setEditItem] = useState(null)
    const [editing, setEditing] = useState(false)
    const [deleteItem, setDeleteItem] = useState(null)
    const [deleteLoading, setDeleteLoading] = useState(false)
    const [tabBarSlot, setTabBarSlot] = useState(null)
    const [useInlineToolbar, setUseInlineToolbar] = useState(false)
    const searchDebounceRef = useRef(null)

    const loadList = useCallback(
        async ({ searchValue = '', skip = 0, append = false } = {}) => {
            if (!projectId) return
            if (append) setLoadingMore(true)
            else {
                setLoading(true)
                setLoadError('')
            }

            const res = await listPrototypes({
                projectId,
                search: searchValue,
                skip,
                limit: PAGE_SIZE,
            })

            if (append) setLoadingMore(false)
            else setLoading(false)

            if (!res.success) {
                if (!append) {
                    setItems([])
                    setLoadError(res.message || 'Failed to load prototypes')
                }
                return
            }

            setIsNext(Boolean(res.isNext))
            setItems((prev) =>
                append ? [...prev, ...(res.result || [])] : res.result || []
            )
        },
        [projectId]
    )

    useEffect(() => {
        loadList({ searchValue: '', skip: 0 })
    }, [loadList])

    useEffect(() => {
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
        searchDebounceRef.current = setTimeout(() => {
            loadList({ searchValue: search.trim(), skip: 0 })
        }, 300)
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current)
            }
        }
    }, [search, loadList])

    useEffect(() => {
        let timer = null
        let retryTimer = null

        const attachToolbar = () => {
            const slot = attachTabBarSlot({
                hostAttr: PROTOTYPE_TAB_BAR_HOST_ATTR,
                slotAttr: PROTOTYPE_TAB_BAR_SLOT_ATTR,
                hostClassName: styles.tabBarHost,
            })
            if (!slot) return false
            setTabBarSlot(slot)
            setUseInlineToolbar(false)
            return true
        }

        if (!attachToolbar()) {
            timer = window.setTimeout(attachToolbar, 0)
            retryTimer = window.setTimeout(() => {
                if (!attachToolbar()) setUseInlineToolbar(true)
            }, 120)
        }

        return () => {
            if (timer) window.clearTimeout(timer)
            if (retryTimer) window.clearTimeout(retryTimer)
            removeTabBarHost(PROTOTYPE_TAB_BAR_HOST_ATTR)
            setTabBarSlot(null)
            setUseInlineToolbar(false)
        }
    }, [projectId])

    const handleCreate = async ({ name, description, file }) => {
        if (readOnly) return
        setCreating(true)
        const uploaded = await uploadPrototypeHtmlFile(file)
        if (!uploaded.success) {
            setCreating(false)
            showActionNotification({
                type: 'error',
                message: uploaded.message || 'Upload failed',
            })
            return
        }

        const res = await createPrototype({
            projectId,
            name,
            description,
            fileUrl: uploaded.publicLink,
            fileName: uploaded.fileName,
            fileSize: uploaded.fileSize,
            designWidth: 1440,
        })
        setCreating(false)

        if (!res.success) {
            showActionNotification({
                type: 'error',
                message: res.message || 'Failed to create prototype',
            })
            return
        }

        setCreateOpen(false)
        showActionNotification({
            type: 'success',
            message: res.message || 'Prototype created',
        })
        if (res.result?._id) {
            onOpen?.(res.result._id)
            return
        }
        await loadList({ searchValue: search.trim(), skip: 0 })
    }

    const handleDelete = (item) => {
        if (readOnly || !canDelete) return
        setDeleteItem(item)
    }

    const handleEdit = (item) => {
        if (readOnly || !item) return
        setEditItem(item)
    }

    const closeEdit = () => {
        if (editing) return
        setEditItem(null)
    }

    const confirmEdit = async ({ name, description }) => {
        if (!editItem?._id || readOnly) return
        setEditing(true)
        try {
            const res = await updatePrototype(editItem._id, {
                name,
                description,
                designWidth: editItem.designWidth,
            })
            if (!res.success) {
                showActionNotification({
                    type: 'error',
                    message: res.message || 'Failed to update',
                })
                return
            }
            showActionNotification({
                type: 'success',
                message: res.message || 'Prototype updated',
            })
            setEditItem(null)
            setItems((prev) =>
                prev.map((item) =>
                    item._id === editItem._id
                        ? {
                              ...item,
                              name,
                              description,
                          }
                        : item
                )
            )
        } finally {
            setEditing(false)
        }
    }

    const closePrototypeDelete = () => {
        if (deleteLoading) return
        setDeleteItem(null)
        setDeleteLoading(false)
    }

    const confirmPrototypeDelete = async () => {
        if (!deleteItem?._id) return
        setDeleteLoading(true)
        try {
            const res = await deletePrototype(deleteItem._id)
            if (!res.success) {
                showActionNotification({
                    type: 'error',
                    message: res.message || 'Failed to delete',
                })
                return
            }
            showActionNotification({
                type: 'success',
                message: res.message || 'Prototype deleted',
            })
            setDeleteItem(null)
            await loadList({ searchValue: search.trim(), skip: 0 })
        } finally {
            setDeleteLoading(false)
        }
    }

    const listToolbar = (
        <div className={styles.tabBarToolbar}>
            <Input
                allowClear
                placeholder="Search prototypes"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={styles.tabBarSearch}
                prefix={<Icon icon="mdi:magnify" width={16} />}
            />
            {!readOnly ? (
                <button
                    type="button"
                    className={styles.createButton}
                    onClick={() => setCreateOpen(true)}
                >
                    <Icon icon="mdi:plus" width={16} />
                    New prototype
                </button>
            ) : null}
        </div>
    )

    return (
        <>
        {tabBarSlot ? createPortal(listToolbar, tabBarSlot) : null}
        <div className={styles.prototypePage}>
            {useInlineToolbar ? (
                <div className={styles.toolbar}>{listToolbar}</div>
            ) : null}

            <div className={styles.content}>
                <PrototypeList
                    items={items}
                    loading={loading}
                    loadError={loadError}
                    onOpen={onOpen}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    readOnly={readOnly}
                    canDelete={!readOnly && canDelete}
                    onRetry={() =>
                        loadList({ searchValue: search.trim(), skip: 0 })
                    }
                />
                {isNext && !loading ? (
                    <div style={{ textAlign: 'center', marginTop: 16 }}>
                        <button
                            type="button"
                            className={styles.ghostButton}
                            disabled={loadingMore}
                            onClick={() =>
                                loadList({
                                    searchValue: search.trim(),
                                    skip: items.length,
                                    append: true,
                                })
                            }
                        >
                            {loadingMore ? <Spin size="small" /> : null}
                            Load more
                        </button>
                    </div>
                ) : null}
            </div>

            {!readOnly ? (
                <>
                    <CreatePrototypeModal
                        open={createOpen}
                        confirming={creating}
                        onCancel={() => setCreateOpen(false)}
                        onSubmit={handleCreate}
                    />
                    <EditPrototypeModal
                        open={Boolean(editItem)}
                        confirming={editing}
                        initialName={editItem?.name || ''}
                        initialDescription={editItem?.description || ''}
                        onCancel={closeEdit}
                        onSubmit={confirmEdit}
                    />
                </>
            ) : null}
        </div>
        {canDelete ? (
            <DeletePopUp
                previewDelete={Boolean(deleteItem)}
                loadingButton={deleteLoading}
                onCancel={closePrototypeDelete}
                onDelete={confirmPrototypeDelete}
                modalTitle="Delete Prototype"
                pageKey="Prototype"
            />
        ) : null}
        </>
    )
}

export default ProjectPrototypeListView

